import { useCallback, useMemo } from 'react'
import { Controller } from 'react-hook-form'

import Button from '@/elements/Button'
import FormTextArea from '@/elements/FormTextArea'
import Form from '@/elements/Form'
import FormInput from '@/elements/FormInput'
import FormSelect from '@/elements/FormSelect'
import FormFile from '@/elements/FormFile'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import toast from '@/utils/toast'
import useAuthStore from '@/stores/authStore'
import useReportFraudForm from '@/hooks/forms/useReportFraudForm'
import { formatAsTitle } from '@/utils/format'
import { AlertTypes } from '@/api/types/Alert'
import { createAlert } from '@/api/alert'
import useBreakpoints from '@/hooks/useBreakpoints'

interface ReportFraudFormProps {
  subjectId: string
}

function ReportFraudForm({ subjectId }: ReportFraudFormProps) {
  const { isLoggedIn } = useAuthStore()
  const { isMobile } = useBreakpoints()
  const { form, resetForm } = useReportFraudForm()

  const typesAsOptions = useMemo(() => {
    return Object.values(AlertTypes)
      .map(alertType => ({
        value: alertType,
        text: formatAsTitle(alertType),
      }))
      .sort((a, b) => a.text.localeCompare(b.text))
  }, [])

  const submitNewAlert = useCallback(async () => {
    const isValid = await form.trigger()

    if (isValid) {
      const values = form.getValues()
      const formData = new FormData()
      formData.append('sourceUrl', values.source)
      values.type.forEach((type: string) => {
        formData.append('issue[]', type)
      })
      formData.append('notes', values.notes || '')
      if (values.screenshot) {
        values.screenshot.forEach((file: File) => {
          formData.append('screenshot', file)
        })
      }

      try {
        const { data: { error } } = await createAlert(subjectId, formData)
        if (error) {
          throw new Error('Failed to create alert')
        }
        toast.success('Alert created successfully!')
        resetForm()
      } catch {
        toast.error('Failed to create alert')
      }
    }
  }, [subjectId, form, resetForm])

  return (
    <Flex
      className='card'
      wfull
      p={!isMobile ? 4 : 3}
      gap={4}
    >
      <Flex inline wrap wfull justify='space-between' gap={3}>
        <Text variant='h3'>Report suspected fraud</Text>
      </Flex>
      <Form
        onSubmit={form.handleSubmit(submitNewAlert)}
        style={{ height: '100%' }}
      >
        <Flex className='form-group'>
          <Controller
            name='type'
            control={form.control}
            render={({ field }) => (
              <FormSelect
                {...field}
                isMulti
                values={typesAsOptions.filter(option => field.value?.includes(option.value))}
                onChange={v => v && field.onChange(v.map(item => item.value))}
                label='Type of issue*'
                placeholder='Select one'
                options={typesAsOptions}
                optionLabel={item => item.text}
                optionValue={item => item.value}
                dirty={!!form.formState.dirtyFields.type}
                error={form.formState.errors.type?.message}
              />
            )}
          />
          <Controller
            name='source'
            control={form.control}
            render={({ field }) => (
              <FormInput
                {...field}
                type='url'
                label='Source URL*'
                placeholder='https://example.com'
                dirty={!!form.formState.dirtyFields.source}
                error={form.formState.errors.source?.message}
              />
            )}
          />
        </Flex>
        <Controller
          name='screenshot'
          control={form.control}
          render={({ field }) => (
            <FormFile
              {...field}
              placeholder='Upload Screenshot'
              files={field.value || []}
              accept='.png, .jpg, .jpeg'
              onClear={newFiles => field.onChange(newFiles)}
              onChange={e => {
                const files = e.target.files
                if (files) {
                  const fileArray = Array.from(files)
                  field.onChange([...(field.value || []), ...fileArray])
                }
              }}
              multiple
              error={form.formState.errors.screenshot?.message}
            />
          )}
        />
        <Controller
          name='notes'
          control={form.control}
          render={({ field }) => (
            <FormTextArea
              {...field}
              charCounterLimit={500}
              label='Additional Notes (Optional)'
              placeholder='Input text'
              dirty={!!form.formState.dirtyFields.notes}
              error={form.formState.errors.notes?.message}
              rows={3}
            />
          )}
        />
        <Flex className='form-controls' mt='auto'>
          <Button
            variant='secondary'
            onClick={() => {
              resetForm()
            }}
          >
            Cancel
          </Button>
          <Button
            variant='accent'
            disabled={!isLoggedIn || !form.formState.isDirty}
            submit
          >
            Submit
          </Button>
        </Flex>
      </Form>
    </Flex>
  )
}

export default ReportFraudForm
